<script lang="ts">
	import type {
		DetailedSelectItem,
		DetailedSelectItemGroup,
		DetailedSelectItemIconProps
	} from '@repo/commons/models'
	import { clickOutside, generateId, hasUniformElementTypeLabels } from '@repo/commons/utils'
	import {
		BaseInput,
		Button,
		ButtonType,
		Avatar,
		Icon,
		HighlightedText,
		OverlayScrollbar,
		Checkbox,
		LoadingSpinner,
		Label
	} from '@repo/commons/components'
	import { fade, scale } from 'svelte/transition'
	import { portal } from 'svelte-portal'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import SelectedItemAppend from './DetailedSelect/SelectedItemAppend.svelte'
	import { tick } from 'svelte'

	const DROPDOWN_MAX_HEIGHT = 300
	const DROPDOWN_GAP = 8

	interface Props {
		id?: string
		isRequired?: boolean
		items?: DetailedSelectItem[]
		selectedItem?: DetailedSelectItem | null
		selectedItems?: DetailedSelectItem[]
		label?: string
		error?: string
		warning?: string
		highlightPattern?: string
		shouldHighlightOptions?: boolean
		searchable?: boolean
		clearable?: boolean
		readonly?: boolean
		placeholder?: string
		bottomButtonText?: string
		showBottomButton?: boolean
		multiSelect?: boolean
		disabled?: boolean
		maxDisplayItems?: number
		maxSelections?: number | null
		requireSearch?: boolean
		minSearchLength?: number // Minimum characters before showing results
		showNoResultsButton?: boolean
		noResultsButtonText?: string
		maxHeight?: number | null
		displayIds?: boolean
		helperText?: string
		tooltip?: TooltipProps
		onSelect?: ((item: DetailedSelectItem | null) => void) | null
		onMultiSelect?:
			| ((items: DetailedSelectItem[], lastSelectedItem: DetailedSelectItem | null) => void)
			| null
		onBlur?: (() => void) | null
		onFocus?: (() => void) | null
		onShowAllClicked?: (() => void) | null
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
		onSearch?: (query: string) => void
		/** When true, `items` are shown as returned by the parent (no title/description client filter). Use with `onSearch` for backend search. */
		remoteItems?: boolean
		/** While the parent is loading the first page / search results (hides premature “no results”). */
		isLoading?: boolean
		/** Infinite scroll: parent indicates more pages exist. */
		hasMore?: boolean
		/** Shown at list bottom while parent loads the next page. */
		loadingMore?: boolean
		/** Distance from bottom of the dropdown viewport to trigger `onLoadMore`. */
		loadMoreThresholdPx?: number
		/** Called when the user scrolls near the bottom; parent runs the next page request. */
		onLoadMore?: (() => void | Promise<void>) | null
		onNoResultsButtonClick?: (searchQuery: string) => void
		onItemIconClick?: (item: DetailedSelectItem, event: MouseEvent | KeyboardEvent) => void
		noResultsRender?: any
		noResultsProps?: Record<string, any>
		avatarBackground?: string | null
		prefixIcon?: { name: string; class?: string; size?: string } | null
		suffixIcon?: DetailedSelectItemIconProps | null
		position?: 'left' | 'right'
		clearSearchOnSelect?: boolean
	}

	let {
		id = generateId(),
		items = [],
		isRequired = false,
		selectedItem = $bindable(null),
		selectedItems = $bindable([]),
		label = '',
		error = '',
		warning = '',
		highlightPattern = '',
		searchable = true,
		clearable = false,
		disabled = false,
		readonly = false,
		placeholder = 'Select an item',
		bottomButtonText = 'See all',
		showBottomButton = false,
		multiSelect = false,
		maxDisplayItems = 3,
		maxHeight = DROPDOWN_MAX_HEIGHT,
		maxSelections = null,
		shouldHighlightOptions = false,
		requireSearch = false,
		minSearchLength = 1,
		showNoResultsButton = false,
		noResultsButtonText = 'Add',
		noResultsRender = null,
		noResultsProps = {},
		displayIds = false,
		helperText = '',
		tooltip = {},
		avatarBackground = $bindable(null),
		onSelect = null,
		onMultiSelect = null,
		onShowAllClicked = null,
		onBlur = () => {},
		onFocus = () => {},
		onHighlightMatch = () => {},
		onSearch = () => {},
		remoteItems = false,
		isLoading = false,
		hasMore = false,
		loadingMore = false,
		loadMoreThresholdPx = 80,
		onLoadMore = null,
		onNoResultsButtonClick = () => {},
		onItemIconClick,
		prefixIcon = null,
		suffixIcon = null,
		position = 'left',
		clearSearchOnSelect = true
	}: Props = $props()

	let container: HTMLElement | null = $state(null)
	let selectWrapper: HTMLLabelElement | null = $state(null)
	let dropdownElement: HTMLDivElement | null = $state(null)
	let isOpen = $state(false)
	let activeItemIndex = $state(-1)
	let searchQuery: string = $state('')
	let inputValue: string = $state('')
	const listboxId = $derived(`${id}-listbox`)
	let loadMoreInFlight = $state(false)

	let dropdownPosition = $state<{
		top?: number
		bottom?: number
		left?: number
		right?: number
		width: number
		openUp: boolean
	}>({ top: 0, left: 0, width: 0, openUp: false })

	const filteredItems = $derived.by(() => {
		const query = (searchable ? inputValue : searchQuery).trim()

		// In requireSearch mode, don't show items if query is below minimum length
		if (requireSearch && query.length < minSearchLength) {
			return []
		}

		if (remoteItems) {
			return items
		}

		if (query === '') {
			// In requireSearch mode, return empty array when no query
			// In regular mode, return all items
			return requireSearch ? [] : items
		}

		// If search value equals the selected item's title, show all options
		// This provides better UX when opening dropdown with pre-selected value
		// But only in regular mode, not in requireSearch mode
		if (
			!multiSelect &&
			!requireSearch &&
			selectedItem &&
			query.toLowerCase() === selectedItem.title.toLowerCase()
		) {
			return items
		}

		const lowerQuery = query.toLowerCase()
		return items.filter(
			(item) =>
				(item.title?.toLowerCase() || '').includes(lowerQuery) ||
				(item.description?.toLowerCase() || '').includes(lowerQuery) ||
				(item.group?.toLowerCase() || '').includes(lowerQuery)
		)
	})
	const navigableItems = $derived(filteredItems.filter(canSelectItem))

	const groupedFilteredItems: DetailedSelectItemGroup[] = $derived.by(() => {
		const itemsByGroup = new Map<string | null, DetailedSelectItem[]>()

		filteredItems.forEach((item) => {
			const name = item.group ?? null
			itemsByGroup.set(name, [...(itemsByGroup.get(name) ?? []), item])
		})

		return [...itemsByGroup].map(([name, items]) => ({ name, items }))
	})
	const shouldHideElementTypeLabels = $derived(hasUniformElementTypeLabels(items))
	const selectedItemLabel = $derived(
		shouldHideElementTypeLabels && selectedItem?.label?.isElementType
			? undefined
			: selectedItem?.label
	)

	const displayValue = $derived.by(() => {
		// If we're searching and the field is searchable, show the search input
		if (searchable && isOpen) {
			return inputValue
		}

		// In requireSearch mode, always show the input value when not open
		if (requireSearch) {
			return inputValue
		}

		if (multiSelect) {
			if (selectedItems.length === 0) return ''
			if (maxDisplayItems && selectedItems.length <= maxDisplayItems) {
				return selectedItems.map((item) => item.title).join(', ')
			}
			return `${selectedItems.length} items selected`
		}
		return selectedItem?.title ?? ''
	})

	const hasReachedMaxSelections = $derived.by(() => {
		return maxSelections !== null && selectedItems.length >= maxSelections
	})

	const shouldShowDropdown = $derived.by(() => {
		if (!isOpen) {
			return false
		}

		// In requireSearch mode, only show if we have results or are searching
		if (requireSearch) {
			return (
				filteredItems.length > 0 ||
				(inputValue.length >= minSearchLength && filteredItems.length === 0)
			)
		}

		// if (!items.length && inputValue.length < minSearchLength) {
		// 	return false
		// }

		// In regular mode, show when open
		return true
	})

	const shouldShowNoResults = $derived.by(() => {
		if (isLoading) {
			return false
		}
		if (!items.length) {
			return true
		}
		const query = searchable ? inputValue : searchQuery
		return !filteredItems.length && query.length >= minSearchLength
	})

	function handleViewportScroll(detail: {
		scrollTop: number
		scrollHeight: number
		clientHeight: number
	}) {
		if (!remoteItems || !onLoadMore || !hasMore || loadingMore || loadMoreInFlight) {
			return
		}
		const { scrollTop, scrollHeight, clientHeight } = detail
		if (scrollHeight <= clientHeight) {
			return
		}
		if (scrollTop + clientHeight < scrollHeight - loadMoreThresholdPx) {
			return
		}
		loadMoreInFlight = true
		void Promise.resolve(onLoadMore()).finally(() => {
			loadMoreInFlight = false
		})
	}

	function calculateDropdownPosition() {
		if (!selectWrapper) return

		const rect = selectWrapper.getBoundingClientRect()
		const spaceBelow = window.innerHeight - rect.bottom
		const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT + DROPDOWN_GAP

		// position:fixed uses viewport coordinates
		dropdownPosition = {
			...(openUp
				? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP }
				: { top: rect.bottom + DROPDOWN_GAP }),
			...(position === 'right' ? { right: window.innerWidth - rect.right } : { left: rect.left }),
			width: rect.width,
			openUp
		}
	}

	function toggleDropdown() {
		if (!isOpen) {
			calculateDropdownPosition()
			// Initialize input value when opening
			if (searchable) {
				if (multiSelect || requireSearch) {
					inputValue = requireSearch ? inputValue : '' // Keep existing value in requireSearch mode
				} else {
					// For single select, show the selected value initially
					// This allows users to see all options before they start typing
					inputValue = selectedItem?.title ?? ''
				}
			}
		}
		isOpen = !isOpen
		if (!isOpen) activeItemIndex = -1
	}

	async function handleInputKeyDown(event: KeyboardEvent) {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			const direction = event.key === 'ArrowDown' ? 1 : -1
			const wasOpen = isOpen

			if (!wasOpen) {
				toggleDropdown()
				await tick()
			}

			if (navigableItems.length === 0) {
				activeItemIndex = -1
				return
			}

			if (!wasOpen) {
				const selectedIndex = navigableItems.findIndex(
					(item) => item.id === (multiSelect ? selectedItems[0]?.id : selectedItem?.id)
				)
				activeItemIndex =
					selectedIndex >= 0 ? selectedIndex : direction > 0 ? 0 : navigableItems.length - 1
			} else {
				if (activeItemIndex < 0) {
					activeItemIndex = direction > 0 ? 0 : navigableItems.length - 1
				} else {
					const nextIndex = activeItemIndex + direction
					activeItemIndex = Math.max(0, Math.min(nextIndex, navigableItems.length - 1))
				}
			}

			await tick()
			dropdownElement
				?.querySelector('[data-keyboard-active="true"]')
				?.scrollIntoView({ block: 'nearest' })
			return
		}

		if (event.key === 'Enter' && isOpen) {
			const activeItem = navigableItems[activeItemIndex]
			if (activeItem) {
				event.preventDefault()
				selectItem(activeItem)
			}
			return
		}

		if (event.key === 'Escape' && isOpen) {
			event.preventDefault()
			isOpen = false
			activeItemIndex = -1
		}
	}

	function selectItem(item: DetailedSelectItem) {
		if (multiSelect) {
			toggleItemSelection(item)
		} else {
			selectedItem = item
			if (requireSearch) {
				// In requireSearch mode, set the input value to the selected item's title
				inputValue = item.title
			} else {
				inputValue = item.title
			}
			isOpen = false
			onSelect?.(item)
		}
	}

	function toggleItemSelection(item: DetailedSelectItem) {
		const isSelected = selectedItems.some((selected) => selected.id === item.id)

		if (isSelected) {
			// Always allow deselection
			selectedItems = selectedItems.filter((selected) => selected.id !== item.id)
		} else {
			// Check if we can add more items
			if (maxSelections === null || selectedItems.length < maxSelections) {
				selectedItems = [...selectedItems, item]
			}
			// If we've reached max selections, don't add the item
		}

		onMultiSelect?.(selectedItems, item)

		if (clearSearchOnSelect) {
			inputValue = ''
			onSearch?.('')
		}
	}

	function isItemSelected(item: DetailedSelectItem): boolean {
		if (multiSelect) {
			return selectedItems.some((selected) => selected.id === item.id)
		}
		return item.id === selectedItem?.id
	}

	function canSelectItem(item: DetailedSelectItem): boolean {
		if (item.disabled) return false
		if (!multiSelect) return true
		if (isItemSelected(item)) return true // Can always deselect
		return !hasReachedMaxSelections // Can select if not at max
	}

	function handleShowAll() {
		onShowAllClicked?.()
	}

	async function handleNoResultsButtonClick() {
		const query = searchable ? inputValue : searchQuery
		const result = onNoResultsButtonClick?.(query.trim())
		isOpen = false

		if (result && typeof result.then === 'function') {
			try {
				await result
			} catch (error) {
				console.error('Error in onNoResultsButtonClick:', error)
			}
		}
	}

	function handleClickOutside(_e: Event) {
		isOpen = false

		// Reset input value to display value when closing
		if (searchable && !requireSearch) {
			if (multiSelect) {
				inputValue = ''
			} else {
				inputValue = selectedItem?.title ?? ''
			}
		}
		// In requireSearch mode, keep the current input value

		handleBlur()
	}

	async function handleBlur() {
		await tick()
		onBlur?.()
	}

	function handleClear() {
		if (multiSelect) {
			selectedItems = []
			onMultiSelect?.(selectedItems, null)
		} else {
			selectedItem = null
			onSelect?.(null)
		}
		inputValue = ''
	}

	function handleInputChange(value: string) {
		inputValue = value
		activeItemIndex = -1

		// Trigger search callback if provided
		onSearch?.(value)

		if (!isOpen && (value.length >= minSearchLength || !requireSearch)) {
			isOpen = true
			calculateDropdownPosition()
		}

		// Close dropdown if in requireSearch mode and input is too short
		if (requireSearch && value.length < minSearchLength && isOpen) {
			isOpen = false
		}
	}

	async function handleInputClick(e: Event) {
		e.stopPropagation()
		if (searchable) {
			if (!isOpen) {
				// In requireSearch mode, only open if we have enough characters
				if (requireSearch) {
					if (inputValue.length >= minSearchLength) {
						toggleDropdown()
					}
				} else {
					toggleDropdown()
				}
			}
		} else {
			const wasOpen = isOpen
			toggleDropdown()
			if (!wasOpen) {
				await tick()
				const firstItem = dropdownElement?.querySelector<HTMLButtonElement>(
					'button.dropdown-item:not(:disabled)'
				)
				;(firstItem ?? dropdownElement)?.focus()
			}
		}
	}

	function handleResize() {
		if (isOpen) {
			calculateDropdownPosition()
		}
	}

	function handleScroll() {
		if (isOpen) {
			calculateDropdownPosition()
		}
	}

	const handleItemIconClick = (item: DetailedSelectItem, event: MouseEvent | KeyboardEvent) => {
		event.stopPropagation()

		onItemIconClick?.(item, event)
	}

	/**
	 * If the error is cleared, recalculate the dropdown position
	 */
	$effect(() => {
		if (!error) {
			calculateDropdownPosition()
		}
	})

	$effect(() => {
		if (isOpen) {
			window.addEventListener('resize', handleResize)
			window.addEventListener('scroll', handleScroll, true)

			return () => {
				window.removeEventListener('resize', handleResize)
				window.removeEventListener('scroll', handleScroll, true)
			}
		}
	})

	// Initialize input value when selected items change
	$effect(() => {
		if (!isOpen && searchable) {
			if (multiSelect) {
				inputValue = ''
			} else {
				inputValue = selectedItem?.title ?? ''
			}
		}
	})
</script>

<svelte:window on:resize={handleResize} on:scroll={handleScroll} />

<div class="custom-select" bind:this={container}>
	<div class="select-wrapper relative" class:non-searchable={!searchable && !readonly}>
		<BaseInput
			bind:element={selectWrapper}
			{highlightPattern}
			size="md"
			{id}
			{label}
			{disabled}
			{placeholder}
			{tooltip}
			{helperText}
			{isRequired}
			value={displayValue}
			readonly={!searchable || readonly}
			{clearable}
			showError={!!error}
			errorMessage={error}
			showWarning={!error && !!warning}
			warningMessage={warning}
			onClear={handleClear}
			onClick={handleInputClick}
			onInput={searchable ? handleInputChange : undefined}
			onFocus={() => onFocus?.()}
			append={requireSearch
				? undefined
				: isLoading
					? { component: LoadingSpinner, props: { size: 24, color: 'rgba(0, 44, 77, 0.5)' } }
					: {
							component: Icon,
							props: {
								name: 'chevronDown',
								class: isOpen ? 'detailed-select-chevron-icon open' : 'detailed-select-chevron-icon'
							}
						}}
			prepend={requireSearch
				? {
						component: Icon,
						props: {
							name: 'search',
							class: 'detailed-select-search-icon'
						}
					}
				: !multiSelect && selectedItem?.imgPayload
					? {
							component: Avatar,
							props: {
								imageSrc: selectedItem.imgPayload,
								fallback: selectedItem.title.charAt(0),
								size: 'sm'
							}
						}
					: prefixIcon
						? { component: Icon, props: prefixIcon }
						: undefined}
			extraAppend={suffixIcon
				? {
						component: SelectedItemAppend,
						props: {
							label: !isOpen && !requireSearch && !multiSelect ? selectedItemLabel : undefined,
							suffixIcon
						}
					}
				: !isOpen && !requireSearch && !multiSelect && selectedItemLabel
					? {
							component: SelectedItemAppend,
							props: { label: selectedItemLabel }
						}
					: null}
			{onHighlightMatch}
			onKeyDown={handleInputKeyDown}
			ariaActiveDescendant={isOpen && navigableItems[activeItemIndex]
				? `${listboxId}-option-${navigableItems[activeItemIndex].id}`
				: undefined}
		/>
	</div>
</div>

{#if shouldShowDropdown}
	<div
		bind:this={dropdownElement}
		use:portal
		use:clickOutside={[handleClickOutside, [...(selectWrapper ? [selectWrapper] : [])]]}
		class="dropdown-card"
		id={listboxId}
		role="listbox"
		tabindex="-1"
		aria-labelledby={`${id}-label`}
		aria-multiselectable={multiSelect}
		style="position: fixed; {dropdownPosition.top != null
			? `top: ${dropdownPosition.top}px;`
			: ''} {dropdownPosition.bottom != null
			? `bottom: ${dropdownPosition.bottom}px;`
			: ''} {dropdownPosition.right != null
			? `right: ${dropdownPosition.right}px;`
			: `left: ${dropdownPosition.left}px;`} width: {dropdownPosition.width}px; z-index: 9999;"
		in:scale={{ duration: 250, start: 0.95 }}
		out:fade={{ duration: 100 }}
	>
		<OverlayScrollbar
			class="dropdown-scrollable"
			onViewportScroll={remoteItems && onLoadMore ? handleViewportScroll : null}
		>
			{#if filteredItems.length}
				<div class="dropdown-items-wrapper">
					{#each groupedFilteredItems as group (group.name)}
						<div class="dropdown-group" role="group" aria-label={group.name ?? undefined}>
							{#if group.name}
								<div class="dropdown-group-label">{group.name}</div>
							{/if}
							{#each group.items as item (item.id)}
								{#key item.id}
									<button
										id={`${listboxId}-option-${item.id}`}
										type="button"
										class="dropdown-item"
										class:active={isItemSelected(item)}
										class:keyboard-active={navigableItems[activeItemIndex]?.id === item.id}
										data-keyboard-active={navigableItems[activeItemIndex]?.id === item.id}
										class:disabled={!canSelectItem(item) || item.disabled}
										onclick={() => canSelectItem(item) && selectItem(item)}
										disabled={!canSelectItem(item) || item.disabled}
										role="option"
										aria-selected={isItemSelected(item)}
										aria-disabled={!canSelectItem(item)}
									>
										{#if multiSelect}
											<div class="item-checkbox" inert>
												<Checkbox checked={isItemSelected(item)} disabled={!canSelectItem(item)} />
											</div>
										{/if}
										{#if item.imgPayload}
											<div class="item-img">
												<Avatar imageSrc={item.imgPayload} fallback={item.title.charAt(0)} />
											</div>
										{:else if item.iconProps}
											<div
												role="button"
												tabindex={-1}
												class="item-img item-icon"
												style="background: {avatarBackground}"
												onclick={(event) => {
													handleItemIconClick(item, event)
												}}
												onkeydown={(event) => {
													if (onItemIconClick && (event.key === 'Enter' || event.key === ' ')) {
														event.stopPropagation()
														handleItemIconClick(item, event)
													}
												}}
											>
												<Icon {...item.iconProps} />
											</div>
										{/if}
										{#if item.startIcon}
											<div
												class={['item-inline-icon', item.startIcon.class].filter(Boolean).join(' ')}
											>
												<Icon name={item.startIcon.name} size={item.startIcon.size} />
											</div>
										{/if}
										<div class="item-content" tabindex="-1">
											<div class="item-content-wrapper">
												<div class="item-title-wrapper">
													<div class="item-title-row">
														{#if shouldHighlightOptions}
															<div class="item-label item-label-ellipsis">
																<HighlightedText
																	text={item.title}
																	pattern={searchable ? inputValue : highlightPattern}
																	{onHighlightMatch}
																/>
															</div>
														{:else}
															<div class="item-label">{item.title}</div>
														{/if}
													</div>
												</div>
												{#if item.description}
													{#if shouldHighlightOptions}
														<div class="item-secondary">
															<HighlightedText
																text={item.description}
																pattern={searchable ? inputValue : highlightPattern}
																{onHighlightMatch}
															/>
														</div>
													{:else}
														<div class="item-secondary">{item.description}</div>
													{/if}
												{/if}
												{#if displayIds}
													<div class="item-id">{item.id}</div>
												{/if}
											</div>
											{#if item.label && !(shouldHideElementTypeLabels && item.label.isElementType)}
												<Label
													label={item.label.text}
													variant={item.label.variant ?? 'default'}
													color={item.label.color}
													icon={item.label.icon}
													size="sm"
													iconSize="sm"
												/>
											{/if}
										</div>
										{#if item.endIcon}
											<div
												class={['item-inline-icon', item.endIcon.class].filter(Boolean).join(' ')}
											>
												<Icon name={item.endIcon.name} size={item.endIcon.size} />
											</div>
										{/if}
									</button>
								{/key}
							{/each}
						</div>
					{/each}
				</div>
			{/if}

			{#if loadingMore}
				<div class="load-more-row">Loading more…</div>
			{/if}

			{#if shouldShowNoResults}
				{#if noResultsRender}
					{@const NoResults = noResultsRender}
					<NoResults
						{...noResultsProps}
						query={inputValue}
						onCloseDropdown={() => (isOpen = false)}
					/>
				{:else}
					<div class="no-results">
						<p class="no-results-text">No results found</p>
						{#if showNoResultsButton}
							<div class="no-results-button">
								<Button
									onclick={handleNoResultsButtonClick}
									buttonText={noResultsButtonText}
									buttonType={ButtonType.TERTIARY}
								/>
							</div>
						{/if}
					</div>
				{/if}
			{/if}
		</OverlayScrollbar>

		{#if showBottomButton && filteredItems.length > 0}
			<div class="bottom-button">
				<Button
					onclick={handleShowAll}
					buttonText={bottomButtonText}
					buttonType={ButtonType.NEUTRAL}
					extraProps="width: 100%;"
				/>
			</div>
		{/if}
	</div>
{/if}

<style>
	.custom-select {
		@apply relative;
	}
	.select-wrapper {
		@apply relative;
	}

	.non-searchable {
		cursor: pointer;
	}

	:global(.detailed-select-chevron-icon) {
		@apply text-[#002C4D80];
		transform: rotate(0deg);
		transition: all 0.3s ease-in-out;
	}
	:global(.detailed-select-chevron-icon.open) {
		transform: rotate(180deg);
	}
	:global(.detailed-select-search-icon) {
		@apply text-[#002C4D80];
	}
	.dropdown-card {
		@apply min-w-[310px] py-2 bg-white rounded-lg shadow-[0px_20px_24px_-4px_rgba(0,0,0,0.08)] shadow-[0px_8px_8px_-4px_rgba(0,0,0,0.04)] border border-[#003a66]/10 flex-col justify-start items-start inline-flex outline-none;
	}
	:global(.dropdown-scrollable.overlay-scrollbar) {
		@apply max-h-[300px] px-0;
	}
	.dropdown-items-wrapper {
		@apply flex flex-col;
	}

	.dropdown-group-label {
		@apply px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[#001D33B2];
	}

	.dropdown-group-label:first-child {
		@apply pt-2;
	}

	button.dropdown-item {
		@apply w-full flex items-center gap-3 px-4 py-2 min-h-12 cursor-pointer hover:bg-[#007ACC1A] transition-colors;
		@apply border-none bg-transparent text-left text-inherit;
		font-family: inherit;
		appearance: none;
		outline: none;
	}

	button.dropdown-item:focus-visible {
		@apply outline outline-2 outline-offset-[-2px] outline-[#007ACC];
	}

	button.dropdown-item.active {
		@apply bg-[#007ACC1A];
	}

	button.dropdown-item.keyboard-active {
		@apply bg-[#007ACC1A] outline outline-2 outline-offset-[-2px] outline-[#007ACC];
	}

	button.dropdown-item.disabled {
		@apply opacity-50 cursor-not-allowed;
	}

	button.dropdown-item.disabled:hover {
		@apply bg-transparent;
	}

	.item-checkbox {
		@apply flex-shrink-0;
	}
	.item-img {
		@apply w-12 h-12 rounded-full flex-shrink-0 object-cover;
	}

	.item-icon {
		@apply flex justify-center items-center;
	}
	.item-inline-icon {
		@apply flex-shrink-0 flex items-center;
	}

	.item-content-wrapper {
		@apply flex flex-col flex-1 max-w-full min-w-0;
	}
	.item-content {
		@apply flex items-center gap-2 flex-1 max-w-full min-w-0;
	}
	.item-title-wrapper {
		@apply flex flex-col items-start gap-1 min-w-0 w-full overflow-hidden;
	}
	.item-title-row {
		@apply flex items-center gap-2 w-full min-w-0;
	}
	.item-title-row .item-label {
		@apply flex-1 min-w-0;
	}
	.item-label {
		@apply text-base text-[#001626E5] w-full truncate;
	}
	.item-label-ellipsis {
		@apply min-w-0;
	}
	.item-id {
		@apply text-xs text-[#001d33]/50 truncate w-full mt-1;
	}
	.item-secondary {
		@apply text-[#001d33]/70 text-sm font-normal leading-tight truncate;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	.bottom-button {
		@apply w-full h-16 px-4 py-2 flex-col justify-start items-start flex;
	}
	.no-results {
		@apply w-full px-4 py-2 flex-col justify-start items-center flex;
	}
	.no-results-text {
		@apply text-sm text-gray-600;
	}
	.no-results-button {
		@apply flex justify-center w-full;
	}
	.load-more-row {
		@apply px-4 py-2 text-sm text-[#001d33]/60 text-center;
	}
</style>
